import { useTranslate } from '@tolgee/react';
import { Col, Row } from 'antd';
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import styled from 'styled-components';

import AppreciateSvg from '../../../assets/icons/AppreciateSvg';
import HeartEmojiSvg from '../../../assets/icons/HeartEmojiSvg';
import StarStruckSvg from '../../../assets/icons/StarStruckSvg';
import theme from '../../../assets/theme';
import useAutoMarkAsPlayedResultRound from '../../../hooks/useAutoMarkAsPlayedResultRound';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import useGameLeaderBoard from '../../../hooks/useGameLeaderboard';
import { useMetrics } from '../../../hooks/useMetrics';
import { useStrengthReviewClickerRegistration } from '../../../hooks/useStrengthReviewClickerRegistration';
import useSwipeResponses from '../../../hooks/useSwipeResponses';
import { LiveActionsDisabledContext } from '../../../providers/LiveActionsDisabledProvider/LiveActionsDisabledProvider';
import { LeaderBoard, Response, Score } from '../../../services/backendService/types';
import { shuffleArray } from '../../../utils/arrayUtils';
import { Button } from '../../_atoms/Button';
import FullScreenHoverNudge from '../../_atoms/FullScreenHoverNudge';
import { IconWrapper } from '../../_atoms/IconWrapper';
import HideResponseToggle from '../../_molecules/HideResponseToggle';
import LazyDrawingResponse from '../../_molecules/LazyDrawingResponse';
import LiveGameImage from '../../_molecules/LiveGameImage';
import StrengthResponseReviewView from '../../_molecules/StrengthResponseReviewView';
import Loader from '../../Loader';
import { Slide } from '../../SlideComponents/slide.types';
import { listSlidesByReferences } from '../../SlideComponents/slideUtil';
import { CenteredContainer } from '../../styled/CenteredContainer';

const LeaderBoardItem = ({
  placement,
  response,
  votes,
  onClick,
}: {
  placement: number;
  response: string;
  votes: number;
  onClick?: () => void;
}) => {
  const { t } = useTranslate();
  const metrics = useMetrics();
  const [isHiding, setIsHiding] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const handleHiding = () => {
    metrics.logEvent('HideResponse', undefined);
    setIsHiding(hidden => !hidden);
  };

  return (
    <LeaderBoardItemContainer
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={() => {
        onClick && onClick();
      }}
    >
      <FullScreenHoverNudge
        isHovering={isHovering}
        isHiding={isHiding}
        handleMouseDown={() => {
          setIsHovering(false);
        }}
      />
      <HideResponseToggle open={isHovering} isHiding={isHiding} onToggle={handleHiding} />
      <Place>{placement}.</Place>
      <LeaderBoardItemQuestion>
        <span>{!isHiding ? response : ''}</span>
      </LeaderBoardItemQuestion>
      <Votes>
        <span>
          {votes} {t('Votes')}
        </span>
      </Votes>
    </LeaderBoardItemContainer>
  );
};

const Votes = styled.div`
  flex: 0;
  min-width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  background-color: white;
  border-radius: 100px;
  & span {
    white-space: nowrap;
    color: ${theme.colors.text.grey};
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const LeaderBoardItemMobile = ({
  placement,
  response,
  votes,
  onClick,
}: {
  placement: number;
  response: string;
  votes: number;
  onClick?: () => void;
}) => {
  const [isHiding, setIsHiding] = useState(false);
  const metrics = useMetrics();
  const { t } = useTranslate();
  const handleHiding = () => {
    metrics.logEvent('HideResponse', undefined);
    setIsHiding(hidden => !hidden);
  };
  return (
    <LeaderBoardContainerMobile onClick={() => onClick && onClick()}>
      <HideResponseToggle open isHiding={isHiding} onToggle={handleHiding} />
      <PlaceMobile>
        <span>{placement}.</span>
      </PlaceMobile>
      <QuestionMobile>
        <span>{!isHiding ? response : ''}</span>
      </QuestionMobile>
      <VotesMobile>
        <span>
          {votes} {t('votes')}
        </span>
      </VotesMobile>
    </LeaderBoardContainerMobile>
  );
};

const QuestionMobile = styled.div`
  border-left: 5px solid ${theme.colors.orange};
  padding-left: 5px;
  span {
    color: ${theme.colors.black};
    font-weight: bold;
    font-size: 24px;
  }
`;
const PlaceMobile = styled.div`
  span {
    color: ${theme.colors.text.grey};
    font-weight: bold;
    font-size: 20px;
  }
`;

const VotesMobile = styled.div`
  span {
    font-size: 14px;
    color: ${theme.colors.text.grey};
    font-weight: bold;
  }
`;

const Place = styled.div`
  font-size: 22px;
  flex: 0;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LeaderBoardItemQuestion = styled.div`
  text-align: center;
  flex: 1;
  width: 70%;

  & span {
    width: 100%;
    text-align: left;
    font-size: 18px;
    font-weight: bold;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LeaderBoardItemContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 20px;
  display: flex;
  background-color: ${theme.colors.white};
  box-shadow: 0px 2px 20px 0px rgba(0, 0, 0, 0.1);
  border-radius: 20px;
  margin-bottom: 10px;
  & > * {
    margin-left: 10px;
    margin-right: 10px;
  }
  cursor: pointer;
  &:hover {
    filter: brightness(95%);
  }
`;

const LeaderBoardContainerMobile = styled(LeaderBoardItemContainer)`
  flex-direction: column;

  & > * {
    margin-top: 5px;
    margin-bottom: 5px;
  }
`;

export const Placement = ({
  place,
  response,
  votes,
  onClick,
  isImage,
  tenantId,
  onImageClicked,
  isAuthenticated,
}: {
  place: number;
  response?: Response;
  votes: number;
  tenantId: string;
  onClick: () => void;
  isImage: boolean;
  onImageClicked?: (img: string) => void;
  isAuthenticated: boolean;
}) => {
  const handleImageClicked = (img: string) => {
    onImageClicked && onImageClicked(img);
  };
  const { t } = useTranslate();
  const metrics = useMetrics();
  const [isHiding, setIsHiding] = useState(false);
  const handleHiding = () => {
    metrics.logEvent('HideResponse', undefined);
    setIsHiding(hidden => !hidden);
  };
  const displayedText =
    !isHiding && response?.response
      ? response?.response.length > 100
        ? `${response?.response.slice(0, 100)}...`
        : response?.response || undefined
      : undefined;
  const [isHovering, setIsHovering] = useState(false);
  const toOrdinal = (num: number): string => {
    if (num === 1) return '1st';
    if (num === 2) return '2nd';
    if (num === 3) return '3rd';
    return num.toString();
  };
  return (
    <FullHeight>
      <PlacementContainer
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onMouseDown={() => setIsHovering(false)}
        $place={place}
        onClick={() => {
          if (onClick && !isImage) {
            metrics.logEvent('Podium.FullScreen.Click', { isImage });
            onClick();
          }
        }}
      >
        <IconWrapper height={'100px'} width={'100px'}>
          {place === 1 && <StarStruckSvg />}
          {place === 2 && <HeartEmojiSvg />}
          {place === 3 && <AppreciateSvg />}
        </IconWrapper>
        <Podium $place={place}>
          <FullScreenHoverNudge
            isHovering={isHovering}
            isHiding={isHiding}
            handleMouseDown={() => {
              setIsHovering(false);
            }}
          />
          <HideResponseToggle
            open={isHovering}
            isHiding={isHiding}
            onToggle={handleHiding}
          />
          {!isImage && (
            <PodiumQuestion>
              {displayedText ? displayedText : `${toOrdinal(place)} place`}
            </PodiumQuestion>
          )}
          {isImage && response && (
            <PodiumImageWrapper>
              {!isHiding && (
                <LiveGameImage
                  imageId={response.response}
                  style={{
                    height: '100%',
                    width: '100%',
                    boxShadow: 'none',
                    maxHeight: '20vh',
                  }}
                  objectFit="contain"
                  fluid
                  isAuthenticated={isAuthenticated}
                  modifiedAt={response.modifiedAt}
                  tenantId={tenantId}
                  onImageClicked={(img: string) => {
                    if (isImage) {
                      metrics.logEvent('Podium.FullScreen.Click', { isImage });
                      onClick();
                      handleImageClicked(img);
                    }
                  }}
                />
              )}
            </PodiumImageWrapper>
          )}
          <PodiumVotes>
            {votes} {t('Votes')}
          </PodiumVotes>
        </Podium>
      </PlacementContainer>
    </FullHeight>
  );
};

const FullHeight = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-end;
`;

const PodiumVotes = styled.p`
  color: ${theme.colors.text.grey};
  margin-bottom: 0px;
  font-weight: bold;
`;

const PodiumQuestion = styled.p`
  font-size: 25px;
  font-weight: bold;
  color: ${theme.colors.black};
  text-align: center;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  width: 100%;
  img {
    max-height: 50vh;
  }
`;

const PodiumImageWrapper = styled.div`
  height: 100%;
  width: 100%;
  img {
    max-height: 50vh;
  }
`;

const getPodiumColor = (place: number) => {
  if (place === 1) return theme.colors.green;
  if (place === 2) return theme.colors.orange;
  if (place === 3) return theme.colors.blue;
  return 0;
};

const Podium = styled.div<{ $place: number }>`
  position: relative;
  flex: 1;
  margin-top: 10px;
  background-color: ${theme.colors.white};
  border-bottom: 5px solid ${({ $place }) => getPodiumColor($place)};
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  border-bottom-right-radius: 10px;
  border-bottom-left-radius: 10px;
  padding: 20px;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;

const PlacementContainer = styled.div<{ $place: number }>`
  display: flex;
  width: 100%;
  flex-direction: column;
  justify-content: flex-end;
  flex: 1 1 auto;
  height: 100%;
  margin-top: ${({ $place }) => ($place - 1) * 30}px;
  @media (max-width: 600px) {
    margin-top: 0px;
  }
  cursor: pointer;
  &:hover {
    filter: brightness(95%);
  }
`;
const PodiumView = ({
  gameId,
  slideId,
  allSlides,
  tenantId,
  onRoundWasPlayedTimeout,
  isAuthenticated,
}: {
  allSlides: Slide[];
  gameId: string;
  tenantId: string;
  slideId: string;
  onRoundWasPlayedTimeout?: () => void;
  isAuthenticated: boolean;
}) => {
  const [showAllResults, setShowAllResults] = useState(false);
  const { setOpenFirstAiResponseHandler } = useContext(LiveActionsDisabledContext);
  const { data, isLoading } = useGameLeaderBoard({
    slideId,
    gameId,
    includeAll: showAllResults,
    isAuthenticated,
  });
  const { t } = useTranslate();
  const isDrawingPodium = useMemo(() => {
    const relatedSlides = listSlidesByReferences(slideId, allSlides);
    const first = relatedSlides[0];

    return first.type === 'canvas';
  }, [allSlides, slideId]);
  const responseSourceSlideId = useMemo(() => {
    const relatedSlides = listSlidesByReferences(slideId, allSlides);
    return relatedSlides[0]?.id;
  }, [allSlides, slideId]);
  const [selectedResponse, setSelectedResponse] = useState<Response | undefined>(
    undefined,
  );
  const [selectedImageResponse, setSelectedImageResponse] = useState<
    string | undefined
  >();
  useAutoFetchMoreResponses({
    responses: data,
    setShowAllResults,
    showAllResults,
    selectedResponse,
  });

  useAutoMarkAsPlayedResultRound({
    shortTimeout: false,
    callback: onRoundWasPlayedTimeout,
  });

  const isLargerThanBreakPoint = useBreakPoint(600);

  const { handleSkipBackResponse, handleSkipForwardResponse } = useSwipeResponses({
    selectedResponse,
    slideId,
    setSelectedResponse,
    podiumResponses: data,
    allSlides,
  });

  useStrengthReviewClickerRegistration({
    selectedResponse,
    setSelectedResponse,
    // The leaderboard array drives the modal's nav order — flatten it
    // to plain Response[] so the close-action computation sees the same
    // order the modal renders.
    navigationResponses: data?.map(d => d.response),
    handleSkipForwardResponse,
    handleSkipBackResponse,
  });

  // Keep selectedResponse in sync with the latest leaderboard data so updates
  // that arrive while the modal is open (e.g. a strength being detected)
  // appear immediately, without the host having to close and reopen.
  useEffect(() => {
    if (!selectedResponse || !data) return;
    const fresh = data.find(d => d.response.id === selectedResponse.id)?.response;
    if (!fresh) return;
    const sameStrength =
      fresh.strength?.description === selectedResponse.strength?.description &&
      fresh.strength?.strengthText === selectedResponse.strength?.strengthText;
    const sameContent = fresh.response === selectedResponse.response;
    if (sameStrength && sameContent) return;
    setSelectedResponse(fresh);
  }, [data, selectedResponse]);

  useEffect(() => {
    if (!slideId || !gameId || !responseSourceSlideId) {
      return;
    }

    const handleOpenFirstResponse = (eventGameId: string, eventSlideId: string) => {
      if (eventGameId !== gameId) return;
      if (eventSlideId !== responseSourceSlideId) return;
      if (data && data.length > 0) {
        setSelectedResponse(data[0].response);
      }
    };

    setOpenFirstAiResponseHandler(() => handleOpenFirstResponse);

    return () => {
      setOpenFirstAiResponseHandler(undefined);
    };
  }, [data, gameId, slideId, responseSourceSlideId, setOpenFirstAiResponseHandler]);

  if (isLoading && !data)
    return (
      <CenteredContainer style={{ paddingTop: 20, width: '100%' }}>
        <Loader />
      </CenteredContainer>
    );

  const top3 = data?.slice(0, 3);
  const first = top3 && top3[0];
  const second = top3 && top3[1];
  const third = top3 && top3[2];
  const rest = data?.slice(3, data.length);
  return (
    <GameActionContainer>
      <StrengthResponseReviewView
        open={selectedResponse !== undefined}
        response={selectedResponse}
        gameId={gameId}
        isDrawing={isDrawingPodium}
        onDone={() => setSelectedResponse(undefined)}
        isAuthenticated={isAuthenticated}
        tenantId={tenantId}
        onPrevious={handleSkipBackResponse}
        onNext={handleSkipForwardResponse}
        responses={data ? data.map(d => d.response) : undefined}
        slideId={responseSourceSlideId}
      />

      <PodiumContainer>
        <Row gutter={[20, 20]} justify={'center'} style={{ flex: '1', width: '100%' }}>
          {data && isLargerThanBreakPoint && (
            <>
              <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
                <Placement
                  key={second?.responseId || '2nd'}
                  place={2}
                  isImage={isDrawingPodium}
                  response={second?.response}
                  tenantId={tenantId}
                  votes={second?.score || 0}
                  onImageClicked={setSelectedImageResponse}
                  onClick={() => second && setSelectedResponse(second.response)}
                  isAuthenticated={isAuthenticated}
                />
              </Col>

              <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
                <Placement
                  key={first?.responseId || '1st'}
                  place={1}
                  tenantId={tenantId}
                  isImage={isDrawingPodium}
                  response={first?.response}
                  onImageClicked={setSelectedImageResponse}
                  onClick={() => first && setSelectedResponse(first.response)}
                  votes={first?.score || 0}
                  isAuthenticated={isAuthenticated}
                />
              </Col>

              <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
                <Placement
                  key={third?.responseId || '3rd'}
                  place={3}
                  isImage={isDrawingPodium}
                  onImageClicked={setSelectedImageResponse}
                  onClick={() => third && setSelectedResponse(third.response)}
                  response={third?.response}
                  tenantId={tenantId}
                  votes={third?.score || 0}
                  isAuthenticated={isAuthenticated}
                />
              </Col>
            </>
          )}
          {!isLargerThanBreakPoint && (
            <>
              <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
                <Placement
                  key={first?.responseId || '1st'}
                  place={1}
                  tenantId={tenantId}
                  isImage={isDrawingPodium}
                  onImageClicked={setSelectedImageResponse}
                  response={first?.response}
                  onClick={() => first && setSelectedResponse(first?.response)}
                  votes={first?.score || 0}
                  isAuthenticated={isAuthenticated}
                />
              </Col>
              {second && (
                <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
                  <Placement
                    key={second?.responseId || '2nd'}
                    onClick={() => second && setSelectedResponse(second.response)}
                    place={2}
                    tenantId={tenantId}
                    isImage={isDrawingPodium}
                    onImageClicked={setSelectedImageResponse}
                    response={second?.response}
                    votes={second.score || 0}
                    isAuthenticated={isAuthenticated}
                  />
                </Col>
              )}
              {third && (
                <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
                  <Placement
                    key={third.responseId || '3rd'}
                    onClick={() => third && setSelectedResponse(third.response)}
                    place={3}
                    tenantId={tenantId}
                    isImage={isDrawingPodium}
                    onImageClicked={setSelectedImageResponse}
                    response={third.response}
                    votes={third?.score || 0}
                    isAuthenticated={isAuthenticated}
                  />
                </Col>
              )}
            </>
          )}
        </Row>
      </PodiumContainer>
      {!isDrawingPodium && (
        <RestContainer>
          {rest?.map((d, i) => {
            if (!isLargerThanBreakPoint) {
              return (
                <LeaderBoardItemMobile
                  key={d.responseId}
                  placement={i + 4}
                  onClick={() => setSelectedResponse(d.response)}
                  response={d.response.response!}
                  votes={d.score}
                />
              );
            }

            return (
              <LeaderBoardItem
                key={d.responseId}
                placement={i + 4}
                onClick={() => setSelectedResponse(d.response)}
                response={d.response.response!}
                votes={d.score}
              />
            );
          })}
        </RestContainer>
      )}

      {isDrawingPodium && showAllResults && (
        <RestContainer>
          <Row gutter={[20, 20]}>
            {rest?.map(d => {
              return (
                <GridItem
                  key={d.responseId}
                  tenantId={tenantId}
                  response={d.response}
                  onImageClicked={setSelectedImageResponse}
                  onResponseClicked={setSelectedResponse}
                />
              );
            })}
          </Row>
        </RestContainer>
      )}
      {data && isLoading && data.length <= 5 && showAllResults && <Loader />}
      <div
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 20,
        }}
      >
        <ShowAllButton
          type="ghost"
          onClick={() => {
            setShowAllResults(r => !r);
          }}
        >
          {showAllResults ? `Show top ${isDrawingPodium ? '3' : '5'}` : t('Show all')}
        </ShowAllButton>
      </div>
    </GameActionContainer>
  );
};

const GridItem = ({
  response,
  onImageClicked,
  tenantId,
  onResponseClicked,
}: {
  response: Response;
  onImageClicked: (imgData: string) => void;
  tenantId: string;
  onResponseClicked: Dispatch<SetStateAction<Response | undefined>>;
}) => {
  const [isHiding, setIsHiding] = useState(false);
  const metrics = useMetrics();
  const handleHiding = () => {
    metrics.logEvent('HideResponse', undefined);
    setIsHiding(hidden => !hidden);
  };
  const [isHovering, setIsHovering] = useState(false);

  return (
    <Col
      key={response.id}
      xs={24}
      sm={12}
      md={8}
      lg={8}
      xl={8}
      xxl={8}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{ height: 300, maxHeight: 300 }}
    >
      <div
        style={{
          height: '100%',
          width: '100%',
          position: 'relative',
          background: 'white',
          borderRadius: 20,
          boxShadow: isHiding ? 'none' : '0px 2px 20px rgba(0,0,0,0.15)',
          filter: !isHovering ? 'none' : 'brightness(95%)',
        }}
      >
        <HideResponseToggle
          open={isHovering}
          isHiding={isHiding}
          onToggle={handleHiding}
        />
        {!isHiding && (
          <>
            <FullScreenHoverNudge
              isHovering={isHovering}
              isHiding={isHiding}
              handleMouseDown={() => {
                setIsHovering(false);
              }}
            />
            <LazyDrawingResponse
              tenantId={tenantId}
              onClick={imgUrl => {
                onResponseClicked(response);
                onImageClicked(imgUrl);
              }}
              response={response}
            />
          </>
        )}
      </div>
    </Col>
  );
};

const useAutoFetchMoreResponses = ({
  setShowAllResults,
  responses,
  showAllResults,
  selectedResponse,
}: {
  responses: LeaderBoard | undefined;
  showAllResults: boolean;
  selectedResponse: Response | undefined;
  setShowAllResults: Dispatch<SetStateAction<boolean>>;
}) => {
  /** autofetched more responses when going through */
  useEffect(() => {
    if (selectedResponse) {
      if (!showAllResults && responses) {
        const isLastIndex = responses?.findIndex(
          r => r.responseId === selectedResponse.id,
        );
        const numberOfResponses = responses.length;
        if (isLastIndex !== -1 && isLastIndex + 1 === numberOfResponses) {
          setShowAllResults(true);
        }
      }
    }
  }, [responses, showAllResults, selectedResponse, setShowAllResults]);
};
const PodiumContainer = styled.div`
  max-width: 1280px;
  width: 90%;
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 20px;
  margin-bottom: 20px;
`;

const RestContainer = styled.div`
  max-width: 1280px;
  margin: auto;
  width: 90%;
  padding: 0px 20px;
`;

const GameActionContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  padding-bottom: 50px;
  width: 100%;
`;

const ShowAllButton = styled(Button)`
  background-color: ${theme.colors.white};

  &:hover,
  &:focus {
    background-color: ${theme.colors.white};
  }
  border: none;
`;

export const PodiumViewStatic = ({
  data,
  isDrawingPodium = false,
}: {
  data: LeaderBoard;
  isDrawingPodium?: boolean;
}) => {
  const top3 = data?.slice(0, 3);
  const first = top3 && top3[0];
  const second = top3 && top3[1];
  const third = top3 && top3[2];
  const rest = data?.slice(3, data.length);
  return (
    <div
      style={{
        height: '100%',
        overflow: 'scroll',
        display: 'grid',
        gridTemplateColumns: '1fr',
      }}
    >
      <PodiumContainer>
        <Row gutter={[20, 20]} justify={'center'} style={{ flex: '1', width: '100%' }}>
          {data && (
            <>
              <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
                <Placement
                  key={second?.responseId || '2nd'}
                  place={2}
                  tenantId={''}
                  isImage={isDrawingPodium}
                  response={second?.response}
                  votes={second?.score || 0}
                  onImageClicked={() => void 0}
                  onClick={() => void 0}
                  isAuthenticated={true}
                />
              </Col>

              <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
                <Placement
                  key={first?.responseId || '1st'}
                  place={1}
                  tenantId={''}
                  isImage={isDrawingPodium}
                  response={first?.response}
                  onImageClicked={() => void 0}
                  onClick={() => void 0}
                  votes={first?.score || 0}
                  isAuthenticated={true}
                />
              </Col>

              <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
                <Placement
                  key={third?.responseId || '3rd'}
                  place={3}
                  isImage={isDrawingPodium}
                  onImageClicked={() => void 0}
                  onClick={() => void 0}
                  response={third?.response}
                  tenantId={''}
                  votes={third?.score || 0}
                  isAuthenticated={true}
                />
              </Col>
            </>
          )}
        </Row>
      </PodiumContainer>
      <RestContainer>
        {rest?.map((d, i) => {
          return (
            <LeaderBoardItem
              key={d.responseId}
              placement={i + 4}
              onClick={() => void 0}
              response={d.response.response!}
              votes={d.score}
            />
          );
        })}
      </RestContainer>
    </div>
  );
};
export const createPodiumData = ({ isDrawing }: { isDrawing: boolean }) => {
  const data: Score[] = [];
  const answers = shuffleArray(isDrawing ? imageUrlVariations : variations);
  for (let i = 0; i < variations.length; i++) {
    const response: Response = {
      id: `responseId-${i}`,
      question: 'What is the meaning of life?',
      response: answers[i],
      uid: `uid-${i}`,
    };
    const score: Score = {
      score: 10,
      questionId: '1',
      question: response,
      responseId: `responseId-${i}`,
      response: response,
    };
    data.push(score);
  }
  return data as LeaderBoard;
};
const variations = [
  'Example answer',
  'An example of an answer',
  'A sample response',
  "Here's an illustrative answer",
  'A demonstration of the answer',
  'An instance of the response',
  'A typical answer',
  'This is how you could answer',
  'An exemplification of the response',
  'A representation of an answer',
  "Here's a possible reply",
  'Consider this response',
  'Imagine the following answer',
  "Here's an instance of a suitable reply",
  'This is a suggested response',
  'Take a look at this answer',
  'An elaboration of the answer',
  "Here's an alternative response",
  'In this case, the answer would be',
  'An example solution',
];
const imageUrlVariations = [
  'https://cdn.pixabay.com/photo/2023/05/03/22/43/tennis-7968714_1280.png',
  'https://cdn.pixabay.com/photo/2023/05/03/22/43/tennis-7968714_1280.png',
];
export default PodiumView;
